import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _classCallCheck from "@babel/runtime/helpers/classCallCheck";
import _createClass from "@babel/runtime/helpers/createClass";
import _possibleConstructorReturn from "@babel/runtime/helpers/possibleConstructorReturn";
import _getPrototypeOf from "@babel/runtime/helpers/getPrototypeOf";
import _inherits from "@babel/runtime/helpers/inherits";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function _callSuper(t, o, e) { return o = _getPrototypeOf(o), _possibleConstructorReturn(t, _isNativeReflectConstruct() ? Reflect.construct(o, e || [], _getPrototypeOf(t).constructor) : o.apply(t, e)); }
function _isNativeReflectConstruct() { try { var t = !Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); } catch (t) {} return (_isNativeReflectConstruct = function _isNativeReflectConstruct() { return !!t; })(); }
import React, { useEffect, useState } from 'react';
import { GlobeIcon } from '@primer/octicons-react';
// @ts-expect-error — the package declares types it doesn't ship; typed below.
import { NetworkViewer as UntypedNetworkViewer } from '@lambdatestincprivate/lambda-har-viewer';
import { LTText, useColorMode } from '@lambdatestincprivate/lt-components';
import { harRefsFor } from '../../../data/pack-store';
import { formatBytes } from '../../format';
import styles from './dock.module.css';

/** The prop surface we use (mirrors kaneai's NetworkHARViewer call). */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var NetworkViewer = UntypedNetworkViewer;

/** A har-viewer crash must not take the page down (kaneai wraps it too). */
var HarErrorBoundary = /*#__PURE__*/function (_React$Component) {
  function HarErrorBoundary() {
    var _this;
    _classCallCheck(this, HarErrorBoundary);
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    _this = _callSuper(this, HarErrorBoundary, [].concat(args));
    _defineProperty(_this, "state", {
      failed: false
    });
    return _this;
  }
  _inherits(HarErrorBoundary, _React$Component);
  return _createClass(HarErrorBoundary, [{
    key: "render",
    value: function render() {
      if (this.state.failed) {
        return /*#__PURE__*/_jsx("div", {
          className: styles.logStates,
          children: /*#__PURE__*/_jsx(LTText, {
            text: "Uh oh! Failed to load Network Logs.",
            variant: LTText.TextVariant.SMALL_REGULAR,
            color: "var(--lt-text-disabled)"
          })
        });
      }
      return this.props.children;
    }
  }], [{
    key: "getDerivedStateFromError",
    value: function getDerivedStateFromError() {
      return {
        failed: true
      };
    }
  }]);
}(React.Component);
var EMPTY_STATE = /*#__PURE__*/_jsxs("div", {
  className: styles.logStates,
  children: [/*#__PURE__*/_jsx(GlobeIcon, {
    size: 24,
    "aria-hidden": "true"
  }), /*#__PURE__*/_jsx(LTText, {
    text: "Network capture isn't part of this evidence pack.",
    variant: LTText.TextVariant.SMALL_REGULAR,
    color: "var(--lt-text-disabled)"
  })]
});

/**
 * Network tab: the pack's HAR (one per attempt) rendered with
 * @lambdatestincprivate/lambda-har-viewer — the same viewer kaneai's
 * Network tab uses. Mounting is deferred 50ms (kaneai's tab-switch jank
 * guard for large HARs); the LAST attempt loads by default.
 */
export function NetworkHarPanel(_ref) {
  var detail = _ref.detail;
  var refs = harRefsFor(detail);
  var network = detail.network;
  var _useColorMode = useColorMode(),
    isDark = _useColorMode.isDark;
  // Deferred mount — rendering >2000 rows stalls the tab switch otherwise.
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showViewer = _useState2[0],
    setShowViewer = _useState2[1];
  useEffect(function () {
    var t = setTimeout(function () {
      return setShowViewer(true);
    }, 50);
    return function () {
      return clearTimeout(t);
    };
  }, []);
  if (refs.length === 0 || network.phase === 'missing') return EMPTY_STATE;
  var body = function () {
    if (network.phase === 'too-large') {
      return /*#__PURE__*/_jsx("div", {
        className: styles.logStates,
        children: /*#__PURE__*/_jsx(LTText, {
          text: "This HAR is ".concat(network.totalBytes !== null ? formatBytes(network.totalBytes) : 'too large', " \u2014 too large to display in the viewer."),
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-disabled)"
        })
      });
    }
    if (network.phase === 'invalid' || network.phase === 'error') {
      return /*#__PURE__*/_jsx("div", {
        className: styles.logStates,
        children: /*#__PURE__*/_jsx(LTText, {
          text: "Couldn't read this pack's network capture.",
          variant: LTText.TextVariant.SMALL_REGULAR,
          color: "var(--lt-text-disabled)"
        })
      });
    }
    if (network.phase !== 'ready' || !network.har || !showViewer) {
      return /*#__PURE__*/_jsx("div", {
        className: styles.logStates,
        "aria-busy": "true",
        children: Array.from({
          length: 5
        }, function (_, i) {
          return /*#__PURE__*/_jsx("span", {
            className: styles.logSkeletonBar,
            "aria-hidden": "true"
          }, i);
        })
      });
    }
    return /*#__PURE__*/_jsx("div", {
      className: styles.harContainer,
      children: /*#__PURE__*/_jsx(HarErrorBoundary, {
        children: /*#__PURE__*/_jsx(NetworkViewer, {
          data: network.har,
          containerClassName: isDark ? 'network-viewer-dark-theme' : undefined,
          options: {
            blacklistFilters: ['XHR', 'Manifest', 'WS', 'Font'],
            downloadButtonTooltipText: 'Download HAR Logs',
            limitFooterStats: true,
            showConnectFilter: false,
            showWaterfall: true,
            showFooter: false,
            isSortEnabled: true,
            testDetails: {
              testName: detail.header.title,
              testId: detail.testId,
              buildName: ''
            }
          }
        })
      })
    });
  }();

  // All attempts merge into one HAR (store-side) — no attempt switcher,
  // matching the Console/Terminal streams' single-timeline presentation.
  return /*#__PURE__*/_jsx(_Fragment, {
    children: body
  });
}